import dayjs from 'dayjs';
import type { FormikProps } from 'formik';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { isQuestionOverflowed } from '@/app/(survey-taking)/_components/SurveyTakingPage/SurveyTakingPage';
import { useToasts } from '@/hooks/useToasts';
import type { Question } from '@/models/question';
import type { Entry, SurveyBase } from '@/models/survey';
import services from '@/services';
import { emailRegex } from '@/utils/common';
import { filterPage } from '@/utils/filter';
import type { SurveyTakingFormValues } from '../[surveyId]/_components/SurveyTaking';
export const useSurveyTaking = () => {
  const {
    t
  } = useTranslation();
  const {
    addErrorToast
  } = useToasts();
  const router = useRouter();
  const [pageNumber, setPageNumber] = useState(0);
  const [errors, setErrors] = useState<Map<string, string>>(new Map());
  const mapToFormikErrors = (errorsMap: Map<string, string>) => Object.fromEntries(Array.from(errorsMap.entries()));
  const handleStartSurvey = async ({
    formik,
    entry
  }: {
    formik: FormikProps<SurveyTakingFormValues>;
    entry?: Entry;
  }) => {
    const timestamp = dayjs().format('YYYY-MM-DD HH:mm:ss.SSS');
    if (entry) {
      if (entry.ended_at) {
        router.replace('/error?type_error=already');
        return;
      }
      if (entry.started_at === null) {
        await services.responseRate.startTimer({
          entryId: entry.id,
          timestamp
        });
      }
    } else {
      formik.setFieldValue('startTimestamp', timestamp);
    }
    setPageNumber(prev => prev + 1);
  };
  const handleChangePage = async ({
    isPreview = false,
    isNextPage,
    currentSurvey,
    formik: formik_0
  }: {
    isPreview?: boolean;
    isNextPage: boolean;
    currentSurvey?: SurveyBase | undefined;
    formik: FormikProps<SurveyTakingFormValues>;
  }): Promise<void> => {
    const scrollToFirstError = (errorsMap_0: Map<string, string>) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const elements = Array.from(document.querySelectorAll<HTMLElement>('[data-error-id]'));
          if (elements.length === 0) {
            const firstErrorId = errorsMap_0.keys().next().value;
            if (firstErrorId) {
              const fallback = document.getElementById(firstErrorId);
              fallback?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }
            return;
          }
          const visibleElements = elements.filter(element => {
            const rect = element.getBoundingClientRect();
            return rect.height > 0 && rect.bottom >= 0 && rect.top <= window.innerHeight;
          });
          const candidates = visibleElements.length ? visibleElements : elements;
          const topMost = candidates.reduce((a, b) => a.getBoundingClientRect().top < b.getBoundingClientRect().top ? a : b);
          topMost.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          const focusable = topMost.querySelector<HTMLElement>('button, [tabindex], input, textarea, a');
          focusable?.focus?.();
        });
      });
    };
    if (!isNextPage) {
      setPageNumber(prev_0 => Math.max(0, prev_0 - 1));
      return;
    }
    try {
      const formikErrors = await formik_0.validateForm();
      const formikErrorsCount = Object.keys(formikErrors ?? {}).length;
      const normalizeFormikValue = (value: unknown): string => {
        if (typeof value === 'string') return value;
        if (Array.isArray(value)) return value.join(', ');
        if (value && typeof value === 'object') {
          try {
            return JSON.stringify(value);
          } catch {
            return String(value);
          }
        }
        return String(value ?? '');
      };
      const normalizedEntries = Object.entries(formikErrors ?? {}).map(([key, value_0]) => [key, normalizeFormikValue(value_0)] as [string, string]);
      const normalizedMap = new Map<string, string>(normalizedEntries);
      setErrors(normalizedMap);
      if (!isPreview) {
        if (currentSurvey?.expires_at && dayjs().isAfter(dayjs(currentSurvey.expires_at))) {
          addErrorToast(t('survey.expired'));
          return;
        }
      }
      if (formikErrorsCount === 0) {
        const pagesCount = formik_0.values.pages?.filter(filterPage).length ?? 0;
        const isOnLastPage = isPreview ? pageNumber === pagesCount : pageNumber === pagesCount && isNextPage;
        if (isOnLastPage) {
          if (!isPreview) {
            const timestamp_0 = dayjs().format('YYYY-MM-DD HH:mm:ss.SSS');
            formik_0.setFieldValue('endTimestamp', timestamp_0);
          } else {
            setPageNumber(prev_1 => prev_1 + 1);
          }
          await formik_0.submitForm();
        } else {
          setPageNumber(prev_2 => prev_2 + 1);
          document.getElementById('stepper')?.scrollIntoView({
            behavior: 'smooth'
          });
        }
      } else {
        scrollToFirstError(normalizedMap);
        addErrorToast(t('error.validating'));
      }
    } catch {
      addErrorToast(t('error.validating'));
    }
  };
  const validateUser = async ({
    id,
    firstName,
    lastName,
    email,
    collector
  }: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    collector?: Map<string, string>;
  }) => {
    const localCollector = collector ?? new Map<string, string>();
    try {
      await Yup.string().matches(emailRegex, `${t('error', {
        ns: 'common'
      })}`).required(`${t('error', {
        ns: 'common'
      })}`).validate(email);
    } catch {
      localCollector.set(`${id}-email`, `${t('validation.email')}`);
    }
    try {
      await Yup.string().required(`${t('error', {
        ns: 'common'
      })}`).validate(firstName);
    } catch {
      localCollector.set(`${id}-firstName`, `${t('validation.firstName')}`);
    }
    try {
      await Yup.string().required(`${t('error', {
        ns: 'common'
      })}`).validate(lastName);
    } catch {
      localCollector.set(`${id}-lastName`, `${t('validation.lastName')}`);
    }
    if (!collector) {
      setErrors(localCollector);
      return mapToFormikErrors(localCollector);
    }
    return undefined;
  };
  const validateQuestionIntoCollector = async (question: Question, collector_0: Map<string, string>) => {
    if (!question?.is_visible) return;
    switch (question?.question_type?.slug) {
      case 'email':
        {
          let validator = Yup.string().matches(emailRegex, `${t('question.emailInvalid')}`);
          if (question?.is_required) validator = validator.required(`${t('question.required')}`);
          try {
            await validator.validate(question?.answer);
          } catch (error_4) {
            collector_0.set(question.id!, String(error_4));
          }
          break;
        }
      case 'text':
      case 'textarea':
      case 'number':
        {
          if (question?.is_required) {
            const answerValue = typeof question?.answer === 'string' ? question.answer.trim() : question?.answer;
            try {
              await Yup.string().required(`${t('question.required')}`).validate(answerValue);
            } catch (error_3) {
              collector_0.set(question.id!, String(error_3));
            }
          }
          break;
        }
      case 'rating-star':
        {
          if (question?.is_required) {
            try {
              await Yup.string().required(`${t('question.required')}`).validate(question?.answer);
            } catch (error_2) {
              collector_0.set(question.id!, String(error_2));
            }
          }
          if (!question?.is_required && question?.answer === undefined) {
            question.answer = null;
          }
          break;
        }
      case 'datetime':
        {
          if (question?.answer !== undefined && question?.answer.toString() === 'Invalid Date') {
            collector_0.set(question.id!, `${t('question.dateInvalid')}`);
          }
          if (question?.is_required) {
            try {
              await Yup.mixed().required(`${t('question.required')}`).validate(question?.answer);
            } catch (error_1) {
              collector_0.set(question.id!, String(error_1));
            }
          }
          break;
        }
      case 'file':
        {
          if (question?.is_required) {
            try {
              await Yup.mixed().required(`${t('question.required')}`).validate(question?.answer);
            } catch (error_0) {
              collector_0.set(question.id!, String(error_0));
            }
          }
          break;
        }
      case 'checkbox':
        {
          const anySelected = question?.linked_choices.some(questionChoice_0 => questionChoice_0?.is_selected);
          if (question?.is_required && !anySelected) {
            collector_0.set(question.id!, `${t('question.required')}`);
          }
          const selectedChoices = (question?.linked_choices ?? []).filter(questionChoice_1 => questionChoice_1?.is_selected);
          for (const choice of selectedChoices.flatMap(questionChoice_2 => questionChoice_2?.linked_questions ?? [])) {
            await validateQuestionIntoCollector(choice, collector_0);
          }
          break;
        }
      case 'radio':
      case 'select':
        {
          if (question?.is_required) {
            try {
              await Yup.string().required(`${t('question.required')}`).validate(question?.select_value);
            } catch (error) {
              collector_0.set(question.id!, String(error));
            }
          }
          const selected = (question?.linked_choices ?? []).find(questionChoice => questionChoice.id === question?.select_value);
          for (const linkedQuestion of selected?.linked_questions ?? []) {
            await validateQuestionIntoCollector(linkedQuestion, collector_0);
          }
          break;
        }
      case 'participant-block':
        {
          if (question?.is_required) {
            try {
              await Yup.array().min(1, `${t('question.required')}`).validate(question?.participant ?? []);
            } catch {
              collector_0.set(question.id!, `${t('participant.block.error')}`);
            }
          }
          for (const participant of question?.participant ?? []) {
            await validateUser({
              id: question.id!,
              firstName: participant?.firstName,
              lastName: participant?.lastName,
              email: participant?.email,
              collector: collector_0
            });
            try {
              await Yup.array().required(`${t('question.required')}`).min(1, `${t('question.required')}`).validate(participant?.sessions);
            } catch {
              collector_0.set(question.id!, `${t('validation.session')}`);
            }
            for (const participantQuestion of participant?.questions ?? []) {
              await validateQuestionIntoCollector(participantQuestion, collector_0);
            }
          }
          break;
        }
      default:
        break;
    }
  };
  const validateform = async ({
    values,
    currentSurvey: currentSurvey_0
  }: {
    values: SurveyTakingFormValues;
    currentSurvey: SurveyBase | undefined;
  }) => {
    const nextErrors = new Map<string, string>();
    if (pageNumber === 0) {
      setErrors(new Map());
      return {};
    }
    const questions = (currentSurvey_0?.is_shufflable ? values.pages[pageNumber - 1].shuffled_questions : values.pages[pageNumber - 1].questions) ?? [];
    const participants = questions.filter(question_0 => question_0.label === 'Participant(s)').flatMap(question_1 => question_1.participant ?? []);
    participants.forEach((participant_0, i) => {
      participants.forEach((participant2, j) => {
        if (participant_0 && participant2 && participant_0?.firstName === values.authentication_form?.firstName && participant_0?.email === values.authentication_form?.email || participant_0?.firstName === participant2?.firstName && participant_0?.email === participant2?.email && i !== j) {
          nextErrors.set('auth', `${t('error.sameUser')}`);
        }
      });
    });
    if (values.authentication_form !== undefined) {
      await validateUser({
        id: 'auth',
        firstName: values.authentication_form.firstName,
        lastName: values.authentication_form.lastName,
        email: values.authentication_form.email,
        collector: nextErrors
      });
    }
    const currentPage = values.pages.filter(filterPage).at(pageNumber - 1);
    const pageQuestions = currentPage?.questions ?? [];
    const shuffledQuestions = currentPage?.shuffled_questions ?? [];
    const filteredQuestions = currentSurvey_0?.is_shufflable ? shuffledQuestions.map(shuffledQuestion => {
      const formikQuestion = pageQuestions.find(pageQuestion => pageQuestion.id === shuffledQuestion.id);
      return formikQuestion ? {
        ...shuffledQuestion,
        ...formikQuestion
      } : shuffledQuestion;
    }) : pageQuestions;
    for (const question_2 of filteredQuestions) {
      if (!isQuestionOverflowed(question_2)) {
        await validateQuestionIntoCollector(question_2, nextErrors);
      }
    }
    setErrors(nextErrors);
    return mapToFormikErrors(nextErrors);
  };
  const validateQuestion = async (question_3: Question) => {
    const collector_1 = new Map<string, string>();
    await validateQuestionIntoCollector(question_3, collector_1);
    setErrors(collector_1);
    return mapToFormikErrors(collector_1);
  };
  return {
    pageNumber,
    setPageNumber,
    errors,
    handleStartSurvey,
    handleChangePage,
    validateUser,
    validateform,
    validateQuestion
  };
};